import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Box, Button } from "@material-ui/core";
import { useGlAllowed } from "../gl-shared/gl-status";
import { draftCard } from "../gl-shared/gl-api";
import { isThinReasoning, whyLength } from "./gl-thin";
import { emitGlEvent } from "./gl-events";
import { getUserIdHash } from "./gl-identity";
import "./gl-why-capture.scss";

// GL-1 §0 per-question state machine phases. PROMPT/REASK are transient (hint
// NOT yet eligible); ADEQUATE/THIN/SKIPPED are the THREE terminal WHY states in
// which the hint affordance becomes eligible.
const PHASE = {
  PROMPT: "PROMPT",
  REASK: "REASK",
  ADEQUATE: "ADEQUATE",
  THIN: "THIN",
  SKIPPED: "SKIPPED"
};
const TERMINAL_WHY = new Set([PHASE.ADEQUATE, PHASE.THIN, PHASE.SKIPPED]);
const isTerminal = (phase) => TERMINAL_WHY.has(phase);
const MAX_HINT_INDEX = 1; // 2 progressive hints (0,1) per GL-1 §3 / #508.

// glError (GL-1 §3) -> gl.hint.blocked reason (GL-1 §1 allowlist).
const BLOCKED_REASON = { cap: "cap", disabled: "flag", unavailable: "error" };
const BLOCKED_COPY = {
  cap: "You've reached today's hint limit for this question.",
  flag: "Hints aren't available right now.",
  error: "Couldn't load a hint. Try again."
};
const TERMINAL_ANNOUNCE = {
  [PHASE.ADEQUATE]: "Reasoning saved. A hint is now available.",
  [PHASE.THIN]: "Reasoning saved. A hint is now available.",
  [PHASE.SKIPPED]: "Skipped. A hint is now available."
};

// Dark-safe default GL API — used ONLY as a jsdom/test fallback; the production
// exam mount passes a real apiService-backed glApi (see gl-service.js).
const DEFAULT_GL_API = {
  requestHint: () => Promise.resolve({ glError: "unavailable", retryable: true })
};

// PHASE -> the wire whyState the card-draft distiller consumes (GL-1 §0).
const WHY_STATE = {
  [PHASE.ADEQUATE]: "adequate",
  [PHASE.THIN]: "thin",
  [PHASE.SKIPPED]: "skipped"
};

/**
 * Inner state machine. It OWNS the WHY hook, so the outer controller can free
 * BOTH React fiber copies of the reasoning by unmounting this subtree (the
 * BFCache fix — a state-clear alone leaves the alternate fiber resident).
 */
const GlWhyInner = ({
  questionId,
  quizId,
  platform,
  userIdHash,
  glApi,
  answerCommitted,
  alreadyTerminalized,
  onTerminalized
}) => {
  const [phase, setPhase] = useState(PHASE.PROMPT);
  // P12(b): the ONLY home for the student's reasoning is this in-memory state.
  const [why, setWhy] = useState("");
  const [hintIndex, setHintIndex] = useState(-1);
  const [hintText, setHintText] = useState("");
  const [hintError, setHintError] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState(""); // A3: aria-live announcements
  const [pendingFocus, setPendingFocus] = useState(null); // "hint" | "status"

  const hintButtonRef = useRef(null);
  const statusRef = useRef(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const subject = userIdHash || getUserIdHash();
  const emit = useCallback(
    (name, payload) =>
      emitGlEvent(name, { userIdHash: subject, questionId, quizId, platform, ...payload }),
    [subject, questionId, quizId, platform]
  );

  // Every terminal transition: record the event, hand the WHY DISPOSITION up to
  // the controller (which holds it in memory until the server grade arrives and
  // then ships it on the card-draft call — the ONE legitimate WHY consumer, per
  // the wire contract; the removed /gl/why sink silently dropped it), CLEAR the
  // raw WHY from this subtree's state (P12(d)), announce (A3), hand focus to the
  // now-eligible hint control (A1). The controller signal also prevents a later
  // BFCache remount from re-terminalizing / double-emitting. Only the ADEQUATE
  // path carries the reasoning text — the server ignores it on thin/skipped, so
  // it never leaves this component for those states (data minimization).
  const terminalize = (nextPhase, text) => {
    setWhy(""); // synchronous post-submit clear
    setPhase(nextPhase);
    setStatusMsg(TERMINAL_ANNOUNCE[nextPhase] || "");
    setPendingFocus("hint");
    if (typeof onTerminalized === "function") {
      onTerminalized({
        whyState: WHY_STATE[nextPhase] || "skipped",
        why: nextPhase === PHASE.ADEQUATE && text ? text : null
      });
    }
  };

  const handleSubmit = () => {
    const text = why;
    const thin = isThinReasoning(text);
    if (phase === PHASE.PROMPT) {
      if (!thin) {
        emit("gl.why.submitted", { whyLen: whyLength(text), thin: false, reaskShown: false });
        terminalize(PHASE.ADEQUATE, text);
        return;
      }
      emit("gl.why.reasked", { whyLen: whyLength(text) });
      setPhase(PHASE.REASK);
      setStatusMsg("Add a little more detail, or keep what you have.");
      return;
    }
    if (phase === PHASE.REASK) {
      if (!thin) {
        emit("gl.why.submitted", { whyLen: whyLength(text), thin: false, reaskShown: true });
        terminalize(PHASE.ADEQUATE, text);
      } else {
        emit("gl.why.submitted", { whyLen: whyLength(text), thin: true, reaskShown: true });
        terminalize(PHASE.THIN, text);
      }
    }
  };

  const handleDismissReask = () => {
    if (phase !== PHASE.REASK) {
      return;
    }
    emit("gl.why.submitted", { whyLen: whyLength(why), thin: true, reaskShown: true });
    terminalize(PHASE.THIN, why);
  };

  const handleSkip = () => {
    if (phase !== PHASE.PROMPT && phase !== PHASE.REASK) {
      return;
    }
    emit("gl.why.skipped", { afterReask: phase === PHASE.REASK });
    terminalize(PHASE.SKIPPED, null); // skip posts no WHY
  };

  // GL-1 §0 coupling — terminalization is bound to the ANSWER COMMIT, not to
  // unmount. The moment the answer commits (UI selection OR a persistence-resume
  // mount that already carries answerGiven), an unresolved WHY terminalizes as an
  // implicit skip — synchronously, exactly once. `alreadyTerminalized` (owned by
  // the controller, survives a BFCache purge) prevents a re-emit on restore.
  useEffect(() => {
    if (answerCommitted && !alreadyTerminalized && !isTerminal(phaseRef.current)) {
      emit("gl.why.skipped", { afterReask: phaseRef.current === PHASE.REASK });
      terminalize(PHASE.SKIPPED, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerCommitted]);

  // A1/A2 — managed focus after a transition removes/updates the focused control.
  useEffect(() => {
    if (pendingFocus === "hint" && hintButtonRef.current) {
      hintButtonRef.current.focus();
    } else if (pendingFocus === "status" && statusRef.current) {
      statusRef.current.focus();
    }
    if (pendingFocus) {
      setPendingFocus(null);
    }
  }, [pendingFocus, phase, hintText, hintError, hintLoading]);

  const hintEligible = isTerminal(phase);
  const nextHintIndex = hintIndex + 1;
  const hintsRemaining = nextHintIndex <= MAX_HINT_INDEX;
  const hintButtonLabel = nextHintIndex === 0 ? "Get a hint" : "Get another hint";

  const requestHint = async () => {
    if (!hintEligible || !hintsRemaining || hintLoading) {
      return;
    }
    const idx = nextHintIndex;
    setHintError(null);
    setHintLoading(true);
    setStatusMsg("Getting a hint…");
    emit("gl.hint.requested", { hintIndex: idx });
    const startedAt = Date.now();
    let response;
    try {
      response = await glApi.requestHint({ questionId, hintIndex: idx });
    } catch (_) {
      response = { glError: "unavailable", retryable: true };
    }
    setHintLoading(false);
    const latencyMs = Math.max(0, Date.now() - startedAt);

    if (response?.glError) {
      const reason = BLOCKED_REASON[response.glError] || "error";
      emit("gl.hint.blocked", {
        reason,
        ...(typeof response.capValue === "number" ? { capValue: response.capValue } : {})
      });
      setHintError(reason);
      setStatusMsg(BLOCKED_COPY[reason]);
      setPendingFocus("hint"); // keep focus on the (still-enabled) retry button
      return;
    }

    emit("gl.hint.served", {
      hintIndex: idx,
      cacheHit: !!response?.cacheHit,
      latencyMs
    });
    // No client-side assisted marking: the server records the serve row
    // (userId, questionId, hintIndex) at the authoritative site — the removed
    // /gl/assisted caller only 404'd (wire-contract "removed" note).
    setHintIndex(idx);
    setHintText(response?.hintText || "");
    setStatusMsg("Hint shown.");
    setPendingFocus("hint");
  };

  const showPromptControls = phase === PHASE.PROMPT || phase === PHASE.REASK;

  return (
    <>
      {/* A3: single polite live region announcing every transition + pending. */}
      <Box ref={statusRef} className="gl-why-status" role="status" aria-live="polite" tabIndex={-1}>
        {statusMsg}
      </Box>

      {showPromptControls && (
        <Box className="gl-why-prompt">
          <label className="gl-why-label" htmlFor="gl-why-input">
            Before you see the answer, why did you pick what you did?
          </label>
          <textarea
            id="gl-why-input"
            className="gl-why-input"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            placeholder="A sentence on your reasoning helps build a better review card."
            rows={3}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {phase === PHASE.REASK && (
            <Box className="gl-why-reask">
              A little more detail — even one specific reason — makes your review
              card sharper. You can add to it or keep what you have.
            </Box>
          )}
          <Box className="gl-why-actions">
            <Button
              className="gl-why-submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={why.trim().length === 0}
            >
              {phase === PHASE.REASK ? "Update reasoning" : "Save reasoning"}
            </Button>
            {phase === PHASE.REASK && (
              <Button className="gl-why-keep" variant="text" onClick={handleDismissReask}>
                Keep what I have
              </Button>
            )}
            <Button
              className="gl-why-skip"
              variant="text"
              onClick={handleSkip}
              aria-label="Skip explaining my reasoning"
            >
              Skip
            </Button>
          </Box>
        </Box>
      )}

      {hintEligible && (
        <Box className="gl-hint">
          {hintText && <Box className="gl-hint-text">{hintText}</Box>}
          {hintError && (
            <Box className="gl-hint-error" role="alert">
              {BLOCKED_COPY[hintError]}
            </Box>
          )}
          {/* A2/5: the request button is NOT disabled while a hint is in flight —
              disabling a focused button drops focus to <body>. It stays enabled +
              aria-busy; a second activation is a no-op (guarded in requestHint),
              so focus is retained and the pending state is announced. */}
          {hintsRemaining && (
            <Button
              ref={hintButtonRef}
              className={`gl-hint-request${hintLoading ? " gl-hint-request--busy" : ""}`}
              variant="outlined"
              onClick={requestHint}
              aria-busy={hintLoading}
              aria-label={hintButtonLabel}
            >
              {hintLoading ? "Getting a hint…" : hintButtonLabel}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

/**
 * GL-3 WHY capture + hint affordance (GL-1 §0/§1/§3). Outer controller: the flag
 * gate and the BFCache PURGE (no client no-store meta is injected — see the note
 * in the effect below). Additive + DARK — when GL is unavailable (flag/audience
 * off) / review / no question, `active` is false, NO effects run and it returns
 * null (flag-off byte-identical, comparator-tested).
 *
 * The mount MUST key by questionId (`<GlWhyCapture key={questionId} .../>`).
 *
 * P12(d) BFCache: `setWhy("")` alone leaves the raw WHY resident in the mounted
 * component's ALTERNATE React fiber, so a genuine `pageshow.persisted=true`
 * restore can recover it. Instead, on `pagehide` we imperatively blank the
 * textarea DOM node AND `flushSync`-UNMOUNT the inner subtree — destroying BOTH
 * fiber copies before the BFCache snapshot — and remount fresh on a persisted
 * `pageshow` (only if the question has not already terminalized, so no re-emit).
 */
const GlWhyCapture = ({
  questionId,
  quizId,
  platform = "web",
  isReview = false,
  userIdHash,
  glApi = DEFAULT_GL_API,
  gradeInfo = null,
  answerCommitted = false
}) => {
  // B2 mount honesty: build flags (sync pre-gate, byte-identical dark when off)
  // AND the server's confirmed GET /gl/status {allowed:true} — a caller the
  // server would 403 never sees the interstitial at all.
  const glAllowed = useGlAllowed();
  const active = glAllowed && !isReview && !!questionId;
  const [purged, setPurged] = useState(false);
  // Survives the purge (the outer is keyed per question, so this resets per
  // question): whether this question already reached a terminal WHY state.
  const terminalizedRef = useRef(false);
  // P12(b): between terminalization and the server grade, the WHY disposition
  // lives ONLY in this in-memory ref (cleared by the pagehide purge below and
  // the moment it ships on the draft call).
  const whyRef = useRef(null);
  const draftFiredRef = useRef(false);
  const [terminalTick, setTerminalTick] = useState(0);
  const onTerminalized = useCallback((payload) => {
    terminalizedRef.current = true;
    if (payload && !whyRef.current) {
      whyRef.current = payload;
    }
    setTerminalTick((t) => t + 1);
  }, []);

  // B5e: the card-draft distiller call, fired exactly once per question when
  // BOTH the WHY disposition (terminalization) and the server grade exist —
  // gradeInfo.gradeEventId is the question_history id the server re-verifies
  // (forged/foreign ids never resolve). Fire-and-forget: drafting must never
  // block the exam flow; a failure just means no card for this question.
  useEffect(() => {
    if (!active || draftFiredRef.current) {
      return;
    }
    if (!whyRef.current || !gradeInfo?.gradeEventId) {
      return;
    }
    draftFiredRef.current = true;
    const { whyState, why } = whyRef.current;
    whyRef.current = null; // P12: the reasoning leaves memory as it ships
    // Fire-and-forget as a single promise chain: Promise.resolve().then()
    // absorbs a SYNCHRONOUS throw from draftCard into the rejection path, and
    // the one .catch absorbs any rejection — drafting can never break the exam
    // flow. (A try/catch around an un-awaited call could not see its rejection,
    // and a promise is always truthy — Sonar S4822/S6544.)
    Promise.resolve()
      .then(() =>
        draftCard({
          questionId,
          gradeEventId: gradeInfo.gradeEventId,
          whyState,
          why
        })
      )
      .catch(() => {});
  }, [active, gradeInfo, terminalTick, questionId]);

  // NOTE: no client "no-store" <meta> is injected. An http-equiv Cache-Control
  // meta is NOT honored by browsers as a caching directive and the gate rejected
  // it as a non-substitute — the authoritative no-store is the response HEADER on
  // the /exam + deck documents (buildspec_develop.yml + scripts/assert-no-store.sh,
  // deploy-gated). P12(d) in-memory safety is the BFCache purge below.

  // P12(d) BFCache purge. Active-only, so a flag-off build installs no listener.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const onPageHide = () => {
      // P12(d): the held WHY disposition is purged with the fibers — a BFCache
      // snapshot must not carry the reasoning either.
      whyRef.current = null;
      const el = document.getElementById("gl-why-input");
      if (el) {
        try {
          el.value = "";
        } catch (_) {
          /* ignore */
        }
      }
      // Unmount the inner synchronously so BOTH fibers are gone before the
      // BFCache snapshot; flushSync forces the commit inside this native event.
      // The textarea DOM node is already blanked above, so the VISIBLE value is
      // gone regardless. The catch is BEST-EFFORT only: if flushSync itself
      // throws (not expected — it is a stable API), a plain setPurged is async
      // and cannot guarantee the fiber unmount lands before a synchronous
      // snapshot. The synchronous fiber-purge guarantee therefore holds on the
      // flushSync path; the fallback degrades to DOM-blanked + a scheduled
      // unmount. Both branches are covered by tests.
      try {
        flushSync(() => setPurged(true));
      } catch (_) {
        setPurged(true);
      }
    };
    const onPageShow = (e) => {
      // Remount fresh ONLY if not already terminalized — a terminalized question
      // must not re-render a prompt or re-emit a skip after restore.
      if (e?.persisted && !terminalizedRef.current) {
        setPurged(false);
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [active]);

  if (!active) {
    return null;
  }

  return (
    <Box component="section" className="gl-why-capture" aria-label="Explain your reasoning">
      {!purged && (
        <GlWhyInner
          questionId={questionId}
          quizId={quizId}
          platform={platform}
          userIdHash={userIdHash}
          glApi={glApi}
          answerCommitted={answerCommitted}
          alreadyTerminalized={terminalizedRef.current}
          onTerminalized={onTerminalized}
        />
      )}
    </Box>
  );
};

export { PHASE, TERMINAL_WHY, MAX_HINT_INDEX, BLOCKED_REASON, isTerminal, GlWhyInner };
export default GlWhyCapture;
